import { ref } from "vue";
import axios from "axios";

const externalAxios = axios.create({
  withCredentials: false,
});

export function useAddressSearch() {
  const suggestions = ref([]);
  const loading = ref(false);
  const debounceTimer = ref(null); // For managing the debounce delay

  const searchInternalAddresses = async (query) => {
    try {
      const response = await axios.get("/api/addresses/search", {
        params: { search: query },
      });
      const results = response.data?.data || [];
      return results.map((item) => ({
        source: "internal",
        id: item.id,
        name: item.title,
        municipality: item.address || "",
        lng: parseFloat(item.lng),
        lat: parseFloat(item.lat),
      }));
    } catch (error) {
      console.error("Internal address search failed:", error);
      return [];
    }
  };

  const searchExternalAddresses = async (query) => {
    try {
      const response = await externalAxios.post(
        process.env.VUE_APP_SEARCH_API_URL,
        {
          query: {
            function_score: {
              query: {
                bool: {
                  must: {
                    multi_match: {
                      query,
                      type: "most_fields",
                      fields: [
                        "name^5",
                        "namegenitive^5",
                        "name.shingle^4",
                        "namegenitive.shingle^4",
                        "name.folded^3",
                        "namegenitive.folded^3",
                        "name.edge^2",
                        "namegenitive.edge^2",
                        "name.trigram^1",
                        "namegenitive.trigram^1",
                        "municipality^3",
                      ],
                      slop: 5,
                    },
                  },
                  must_not: [
                    {
                      match: {
                        namestatus: "istorinis",
                      },
                    },
                  ],
                },
              },
              functions: [
                {
                  filter: { term: { localtype: "Gyvenvietės" } },
                  weight: 1.34,
                },
                { filter: { term: { localtype: "adresas" } }, weight: 1.1 },
              ],
            },
          },
          sort: ["_score", { gyvsk: "desc" }, "name"],
          from: 0,
          size: 20,
        },
        {
          headers: {
            "Content-Type": "text/plain",
          },
        },
      );

      const results = response.data.hits?.hits || [];
      return results.map((item) => ({
        source: "external",
        name: item._source.name,
        municipality: item._source.municipality,
        lng: item._source.LOCATIONX,
        lat: item._source.LOCATIONY,
      }));
    } catch (error) {
      console.error("Address search failed:", error);
      return [];
    }
  };

  const searchAddress = (query) => {
    if (query.length < 3) {
      // Clear suggestions if fewer than 3 characters
      suggestions.value = [];
      return;
    }

    // Debounce logic: clear any existing timer
    if (debounceTimer.value) {
      clearTimeout(debounceTimer.value);
    }

    // Set a new debounce timer
    debounceTimer.value = setTimeout(async () => {
      loading.value = true;

      try {
        const [internalResults, externalResults] = await Promise.all([
          searchInternalAddresses(query),
          searchExternalAddresses(query),
        ]);
        suggestions.value = [...internalResults, ...externalResults];
      } finally {
        loading.value = false;
      }
    }, 500); // Delay in milliseconds
  };

  const selectAddress = (suggestion) => {
    suggestions.value = []; // Clear suggestions
    return {
      address:
        suggestion.source === "internal"
          ? suggestion.name
          : suggestion.name + " (" + suggestion.municipality + ")",
      lng: parseFloat(suggestion.lng),
      lat: parseFloat(suggestion.lat),
    };
  };

  return {
    suggestions,
    loading,
    searchAddress,
    selectAddress,
  };
}
